<template>
    <div>
        <template v-if="!isAdmin">
            <PermissionDenied />
        </template>
        <template v-else>
            <vs-tabs v-model="activeTab">
                <vs-tab label="List" icon-pack="feather" icon="icon-list">
                    <div class="mt-6">
                        <!-- Table Section Header -->
                        <vs-row class="mb-4">
                            <vs-col vs-w="6">
                                <h4 class="mt-2">Vendor List</h4>
                            </vs-col>
                            <vs-col vs-w="6" class="text-right flex justify-end">
                                <vs-button v-if="canCreate" color="primary" type="filled" @click="activeTab = 1; resetForm();" icon-pack="feather" icon="icon-plus" class="mr-2">Add New</vs-button>
                                <vx-tooltip text="Refresh Data" color="primary">
                                    <vs-button color="primary" type="filled" @click="loadData" :disabled="loading">
                                        <font-awesome-icon :icon="redoIcon" />
                                    </vs-button>
                                </vx-tooltip>
                            </vs-col>
                        </vs-row>

                        <!-- Search and Filter Row -->
                        <vs-row class="mb-3">
                            <vs-col vs-xs="12" vs-sm="9" vs-lg="9" class="px-0 pr-2">
                                <vs-input
                                    class="w-full"
                                    placeholder="Search by vendor code or name..."
                                    v-model="filters.search"
                                />
                            </vs-col>
                            <vs-col vs-xs="12" vs-sm="3" vs-lg="3" class="px-0">
                                <InputSelect
                                    :model.sync="filters.filter_status"
                                    label=""
                                    placeholder="Filter by Status"
                                    container_class="w-full"
                                    :options="status_options"
                                />
                            </vs-col>
                        </vs-row>
                        
                        <vx-card>
                            <div v-if="loading" class="text-center p-4">
                                <h3>Loading Data.. Please wait..</h3>
                            </div>
                            
                            <vs-table :data="results" slot="no-body" :hoverFlat="true" stripe v-if="!loading" noDataText="">
                                <vs-tr>
                                    <vs-th style="min-width: 80px;">S.No</vs-th>
                                    <vs-th style="min-width: 150px;">Vendor Code</vs-th>
                                    <vs-th style="min-width: 300px;">Vendor Name</vs-th>
                                    <vs-th style="min-width: 120px;">Vendor Status</vs-th>
                                    <vs-th style="min-width: 100px;" v-if="canEdit">Action</vs-th>
                                </vs-tr>
                                <template v-if="results.length > 0">
                                    <template v-for="(item, index) in results">
                                        <vs-tr :key="index">
                                            <vs-td>
                                                {{ filters.offset + index + 1 }}
                                            </vs-td>
                                            <vs-td>
                                                {{ item.code }}
                                            </vs-td>
                                            <vs-td>
                                                {{ item.name }}
                                            </vs-td>
                                            <vs-td>
                                                <span :class="(item.is_active !== undefined ? item.is_active : item.status) ? 'text-success' : 'text-danger'">
                                                    {{ (item.is_active !== undefined ? item.is_active : item.status) ? 'Active' : 'Inactive' }}
                                                </span>
                                            </vs-td>
                                            <vs-td v-if="canEdit">
                                                <vs-button @click="editItem(item)" color="primary" type="border" icon="icon-edit" icon-pack="feather" size="small"></vs-button>
                                            </vs-td>
                                        </vs-tr>
                                    </template>
                                </template>
                                <template v-else>
                                    <vs-tr>
                                        <vs-td :colspan="canEdit ? 5 : 4" class="text-center p-4">
                                            <p class="text-muted mb-0" v-if="has_active_filters">No results found for the current search/filter criteria.</p>
                                            <p class="text-muted mb-0" v-else>No data available</p>
                                        </vs-td>
                                    </vs-tr>
                                </template>
                            </vs-table>
                        </vx-card>
                        
                        <!-- Pagination -->
                        <template v-if="total_results">
                            <vs-row class="mt-3 text-center">
                                <vs-pagination :total="total_pages" v-model="current_page" goto class="mx-auto"></vs-pagination>
                            </vs-row>
                        </template>
                    </div>
                </vs-tab>

                <vs-tab v-if="canCreate || canEdit" :label="editingId ? 'Edit' : 'Add'" icon-pack="feather" :icon="editingId ? 'icon-edit' : 'icon-plus'">
                    <div class="mt-6">
                        <!-- Form Section -->
                        <vx-card noShadow noRound>
                            <div class="mb-4">
                                <h3 class="mb-2">{{ editingId ? 'Edit Vendor' : 'Create Vendor' }}</h3>
                                <p class="text-muted mb-4" v-if="!editingId">Fill in the details below to create a new vendor entry.</p>
                                <p class="text-muted mb-4" v-else>Update the vendor details below.</p>
                            </div>
                            
                            <vs-row>
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="4" class="mb-4">
                                    <vs-input
                                        v-model="formData.code"
                                        label="Vendor Code *"
                                        placeholder="V001"
                                        class="w-full"
                                        :disabled="true"
                                    />
                                    <small class="block text-warning mt-1">
                                        Note: Auto generated
                                    </small>
                                    <small class="block text-danger mt-1" v-if="api_errors.code">
                                        {{ (api_errors.code || []).join(', ') }}
                                    </small>
                                </vs-col>
                                
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="4" class="mb-4">
                                    <vs-input
                                        v-model="formData.name"
                                        label="Vendor Name *"
                                        placeholder="e.g., Kumar Construction"
                                        class="w-full"
                                    />
                                    <small class="block text-danger mt-1" v-if="api_errors.name">
                                        {{ (api_errors.name || []).join(', ') }}
                                    </small>
                                </vs-col>
                                
                                <vs-col vs-xs="12" vs-sm="6" vs-lg="4" class="mb-4">
                                    <label class="vs-input--label block mb-2">Vendor Status *</label>
                                    <div class="flex align-center">
                                        <vs-radio v-model="formData.is_active" vs-name="is_active" :vs-value="true" class="mr-4">Active</vs-radio>
                                        <vs-radio v-model="formData.is_active" vs-name="is_active" :vs-value="false">Inactive</vs-radio>
                                    </div>
                                    <small class="block text-danger mt-1" v-if="api_errors.is_active">
                                        {{ (api_errors.is_active || []).join(', ') }}
                                    </small>
                                </vs-col>
                            </vs-row>
                            
                            <vs-row class="mt-4">
                                <vs-col vs-xs="12" vs-sm="12" vs-lg="12" class="text-right">
                                    <vs-button @click="resetForm(); activeTab = 0;" color="warning" type="flat" class="mr-2" v-if="editingId">
                                        Cancel
                                    </vs-button>
                                    <vs-button @click="save" :disabled="submit_disabled" color="primary" type="filled">
                                        {{ editingId ? 'Update' : 'Save' }}
                                    </vs-button>
                                </vs-col>
                            </vs-row>
                        </vx-card>
                    </div>
                </vs-tab>
            </vs-tabs>
        </template>
    </div>
</template>

<script>
import { faRedo } from '@fortawesome/free-solid-svg-icons';
import { Request } from '../../utils/requests';

export default {
    data() {
        let offset = parseInt(this.$route.query.offset || 0);
        let limit = parseInt(this.$route.query.limit || 20);
        
        let current_page = offset == 0 ? 1 : Math.ceil(offset / limit) + 1;
        return {
            results: [],
            loading: false,
            submit_disabled: false,
            api_errors: {},
            editingId: null,
            activeTab: 0,
            total_results: null,
            current_page: current_page,
            filters: {
                search: this.$route.query.search || null,
                filter_status: this.$route.query.filter_status || null,
                offset: offset,
                limit: limit
            },
            status_options: [
                { value: null, label: 'All Status' },
                { value: 'true', label: 'Active' },
                { value: 'false', label: 'Inactive' },
            ],
            formData: {
                code: null,
                name: null,
                is_active: true
            }
        }
    },
    computed: {
        redoIcon: () => faRedo,
        isAdmin() {
            return this.$store.state.auth.userRole == 'admin' || this.hasAccess('read', 'master_year', 'any');
        },
        canCreate() {
            return this.$store.state.auth.userRole == 'admin' || this.hasAccess('create', 'master_year', 'all');
        },
        canEdit() {
            return this.$store.state.auth.userRole == 'admin' || this.hasAccess('edit', 'master_year', 'all');
        },
        total_pages() {
            if(this.total_results == 0 || this.filters.limit == 0)  return 1
            return Math.ceil(this.total_results/this.filters.limit)
        },
        has_active_filters() {
            return !!(this.filters.search || this.filters.filter_status)
        }
    },
    watch: {
        filters: {
            deep: true,
            handler(newV) {
                if (this.current_page == null) return;

                if (this.timer) clearTimeout(this.timer);

                this.timer = setTimeout(() => {
                    this.$router.push({ 
                        name: this.$route.name, 
                        query: {
                            offset: newV.offset,
                            limit: newV.limit,
                            search: newV.search || undefined,
                            filter_status: newV.filter_status || undefined
                        } 
                    });
                }, 800);
            }
        },
        "$route.query"(newV) {
            this.loadData();
        },
        current_page(newV, oldV) {
            if (newV == oldV) return;
            this.filters.offset = (newV - 1) * this.filters.limit;
        }
    },
    mounted() {
        this.init()
    },
    methods: {
        loadData() {
            this.loading = true
            this.$vs.loading()
            
            let url = this.$store.state.apiendpoints.master_vendor
            
            let payload = {
                offset: this.filters.offset,
                limit: this.filters.limit
            }
            if (this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id) {
                payload.organisation = this.$store.state.organisation.organisation.id
            }
            if (this.filters.search) payload.search = this.filters.search
            if (this.filters.filter_status !== null && this.filters.filter_status !== undefined) payload.is_active = this.filters.filter_status
            
            Request(this, 'GET', url, payload).then(
                (res) => {
                    // Handle response structure: {status: true, data: {count: X, results: [...]}}
                    if (res && res.data && res.data.results) {
                        this.results = res.data.results
                        this.total_results = res.data.count || 0
                    } else if (res && res.data && Array.isArray(res.data)) {
                        this.results = res.data
                        this.total_results = res.data.length
                    } else if (Array.isArray(res)) {
                        this.results = res
                        this.total_results = res.length
                    } else if (res && res.results) {
                        this.results = res.results
                        this.total_results = res.count || res.results.length
                    } else {
                        this.results = []
                        this.total_results = 0
                    }
                    if (this.filters.search) {
                        const q = this.filters.search.toLowerCase()
                        this.results = this.results.filter(item =>
                            (item.code && item.code.toLowerCase().includes(q)) ||
                            (item.name && item.name.toLowerCase().includes(q))
                        )
                        this.total_results = this.results.length
                    }
                    if (this.filters.filter_status !== null && this.filters.filter_status !== undefined) {
                        const activeFilter = this.filters.filter_status === 'true'
                        this.results = this.results.filter(item => {
                            const isActive = item.is_active !== undefined ? Boolean(item.is_active) : Boolean(item.status)
                            return isActive === activeFilter
                        })
                        this.total_results = this.results.length
                    }
                    this.calculate_page()
                    this.loading = false
                    this.$vs.loading.close()
                },
                (err) => {
                    this.loading = false
                    this.$vs.loading.close()
                    this.$vs.notify({
                        time: 3000,
                        title: 'Error',
                        text: "An error occurred while loading data. Please try again!",
                        iconPack: 'feather',
                        icon: 'icon-alert-circle',
                        color: 'danger'
                    })
                }
            )
        },
        save() {
            if (!this.formData.code || !this.formData.name) {
                this.$vs.notify({
                    time: 3000,
                    title: 'Error',
                    text: "Please fill all required fields!",
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                })
                return
            }
            
            if (!this.$store.state.organisation.organisation || !this.$store.state.organisation.organisation.id) {
                this.$vs.notify({
                    time: 3000,
                    title: 'Error',
                    text: "Organisation not found!",
                    iconPack: 'feather',
                    icon: 'icon-alert-circle',
                    color: 'danger'
                })
                return
            }
            
            this.submit_disabled = true
            this.$vs.loading()
            
            let url = this.$store.state.apiendpoints.master_vendor
            let method = 'POST'
            
            if (this.editingId) {
                url += this.editingId + '/'
                method = 'PUT'
            }
            
            let payload = {
                organisation: this.$store.state.organisation.organisation.id,
                code: this.formData.code,
                name: this.formData.name,
                is_active: this.formData.is_active
            }
            
            Request(this, method, url, null, payload).then(
                (res) => {
                    this.submit_disabled = false
                    this.$vs.loading.close()
                    this.$vs.notify({
                        time: 3000,
                        title: 'Success',
                        text: this.editingId ? "Updated Successfully!" : "Saved Successfully!",
                        iconPack: 'feather',
                        icon: 'icon-check-circle',
                        color: 'success'
                    })
                    this.resetForm()
                    this.loadData()
                    this.activeTab = 0
                },
                (err) => {
                    this.submit_disabled = false
                    this.$vs.loading.close()
                    if (err.error) {
                        this.api_errors = err.error
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error',
                            text: "Please fix the errors and try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    } else {
                        this.$vs.notify({
                            time: 3000,
                            title: 'Error',
                            text: "An unknown error occurred! Please try again!",
                            iconPack: 'feather',
                            icon: 'icon-alert-circle',
                            color: 'danger'
                        })
                    }
                }
            )
        },
        resetForm() {
            this.editingId = null
            this.formData = {
                code: null,
                name: null,
                is_active: true
            }
            this.api_errors = {}
            this.generateCode()

            // Scroll to top
            if (this.$el) {
                this.$el.scrollIntoView({ behavior: 'smooth' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
        editItem(item) {
            this.editingId = item.id
            this.activeTab = 1
            this.formData = {
                code: item.code,
                name: item.name,
                is_active: item.is_active !== undefined ? item.is_active : (item.status || false)
            }
            // Scroll to top
            if (this.$el) {
                this.$el.scrollIntoView({ behavior: 'smooth' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
        calculate_page() {
            if (this.filters.offset == 0) {
                this.current_page = 1;
            } else {
                this.current_page = Math.ceil(this.filters.offset / this.filters.limit) + 1;
            }
        },
        init() {
            let offset = parseInt(this.$route.query.offset || 0);
            let limit = parseInt(this.$route.query.limit || 20);
            this.filters.offset = offset;
            this.filters.limit = limit;
            this.filters.search = this.$route.query.search || null;
            this.filters.filter_status = this.$route.query.filter_status || null;
            this.calculate_page();
            
            if (!this.$route.query.offset || !this.$route.query.limit) {
                this.$router.replace({ 
                    name: this.$route.name, 
                    query: { 
                        ...this.$route.query,
                        offset: this.filters.offset, 
                        limit: this.filters.limit 
                    } 
                });
            }
            
            this.loadData();
            this.generateCode();
        },
        generateCode() {
            if (this.editingId) return

            let url = this.$store.state.apiendpoints.master_vendor
            let payload = {
                limit: 1000
            }
            if (this.$store.state.organisation.organisation && this.$store.state.organisation.organisation.id) {
                payload.organisation = this.$store.state.organisation.organisation.id
            }
            
            Request(this, 'GET', url, payload).then(
                (res) => {
                    let items = []
                    if (res && res.data && res.data.results) {
                        items = res.data.results
                    } else if (res && res.data && Array.isArray(res.data)) {
                        items = res.data
                    } else if (Array.isArray(res)) {
                        items = res
                    } else if (res && res.results) {
                        items = res.results
                    }

                    let maxNum = 0
                    items.forEach(item => {
                        if (item.code && item.code.startsWith('V')) {
                            let numStr = item.code.replace(/^\D+/, '')
                            let num = parseInt(numStr)
                            if (!isNaN(num) && num > maxNum) {
                                maxNum = num
                            }
                        }
                    })

                    let nextNum = maxNum + 1
                    this.formData.code = 'V' + nextNum.toString().padStart(3, '0')
                }
            )
        }
    }
}
</script>

<style lang="scss" scoped>
.text-muted {
    color: #6c757d;
    font-size: 0.9rem;
}

.text-success {
    color: #28a745;
    font-weight: 500;
}

.text-danger {
    color: #dc3545;
    font-weight: 500;
}

.text-warning {
    color: #FF9F43;
    font-weight: 500;
}

.mb-4 {
    margin-bottom: 1.5rem;
}

.mb-2 {
    margin-bottom: 0.5rem;
}

.mt-2 {
    margin-top: 0.5rem;
}

.mr-4 {
    margin-right: 1rem;
}

.mt-4 {
    margin-top: 1.5rem;
}

.flex {
    display: flex;
}

.justify-between {
    justify-content: space-between;
}

.items-center {
    align-items: center;
}
</style>

